import { Component } from '@theme/component';
import { fetchConfig, onAnimationEnd } from '@theme/utilities';
import { CartAddEvent, CartErrorEvent, ThemeEvents } from '@theme/events';
import { cartPerformance } from '@theme/performance';

const ERROR_DISPLAY_MS = 10000;
const ERROR_REENABLE_MS = 1000;

/**
 * @typedef {Object} Variant
 * @property {number} id
 * @property {number} price
 * @property {number} [compare_at_price]
 * @property {boolean} available
 * @property {string} option1
 * @property {string} [option2]
 * @property {string} [option3]
 * @property {string} [sku]
 * @property {{ src?: string, alt?: string } | null} [featured_image]
 */

/**
 * @typedef {{ opt1: boolean, opt2: boolean, opt3: boolean, isComplementary?: boolean }} OptionMatches
 */

/**
 * @typedef {Object} BundleProductState
 * @property {number} variantId
 * @property {number} productId
 * @property {string} productHandle
 * @property {number} price
 * @property {string} sku
 * @property {boolean} isSelected
 * @property {OptionMatches} optionMatches - per-option match state against PDP
 * @property {Variant[]} variants
 */

/**
 * Bundle offer component: current PDP product + up to 2 additional products
 * from a product-level JSON metafield with auto-matching and dynamic discounts.
 * Supports products with multiple variant options such as Config + Größe.
 * @extends {Component}
 */
export class BuyTheBundleComponent extends Component {
  /** @type {AbortController | null} */
  #abortController = null;
  /** @type {number[]} */
  #resetTimeouts = [];
  /** @type {number | undefined} */
  #errorTimeout;
  /** @type {number} */
  #primaryVariantId = 0;
  /** @type {number} */
  #primaryPrice = 0;
  /** @type {string} */
  #primarySku = '';
  /** @type {{ option1: string, option2: string, option3: string }} */
  #pdpOptions = { option1: '', option2: '', option3: '' };
  /** @type {boolean} */
  #pdpIsDefaultTitle = false;
  /** @type {Map<number, BundleProductState>} */
  #bundleProducts = new Map();
  /** @type {Map<number, Variant[]>} */
  #bundleVariants = new Map();
  /** @type {Map<number, Map<number, number>>} */
  #bundleEstimatedRetailPrices = new Map();
  /** @type {Map<number, { productTitle: string, variants: Map<number, string> }>} */
  #bundleVariantNames = new Map();
  /** @type {{ productTitle: string, variants: Map<number, string> } | null} */
  #primaryVariantNames = null;
  /** @type {Variant[]} */
  #primaryVariants = [];
  /** @type {Map<number, number>} */
  #primaryEstimatedRetailPrices = new Map();
  /** @type {number} */
  #discount2 = 0;
  /** @type {number} */
  #discount3 = 0;

  connectedCallback() {
    super.connectedCallback();

    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    this.#parseConfig();
    this.#parseBundleVariants();
    this.#parseBundleMetafields();
    this.#parsePrimaryVariants();
    this.#parsePrimaryMetafields();
    this.#parsePrimaryVariantNames();
    this.#initBundleProducts();

    const primaryProduct = /** @type {HTMLElement | null} */ (this.refs.primaryProduct);
    if (primaryProduct instanceof HTMLElement) {
      this.#primaryVariantId = parseInt(primaryProduct.dataset.variantId || '0', 10);
      this.#primaryPrice = parseInt(primaryProduct.dataset.price || '0', 10);
      this.#primarySku = primaryProduct.dataset.sku || '';
      this.#setBundleVisibility(primaryProduct.dataset.available === 'true');
    }

    this.#initMobileToggle(signal);
    this.#initCheckboxes(signal);
    this.#initCustomDropdowns(signal);
    this.#listenToPdpVariantChange(signal);

    const addToCartButton = /** @type {HTMLButtonElement | null} */ (this.refs.addToCartButton);
    if (addToCartButton) {
      addToCartButton.addEventListener('click', (/** @type {Event} */ e) => this.#handleAddToCart(e), { signal });
    }

    const primaryInputs = this.#getOptionInputs(0);
    if (primaryInputs.length > 0 && this.#primaryVariants.length > 0) {
      this.#updateOptionAvailability(0, this.#readSelectedOptions(primaryInputs), this.#primaryVariants);
    }

    for (const [i, variants] of this.#bundleVariants) {
      const inputs = this.#getOptionInputs(i);
      if (inputs.length > 0) this.#updateOptionAvailability(i, this.#readSelectedOptions(inputs), variants);
    }

    this.#updateItemCount();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController?.abort();
    this.#abortController = null;
    for (const id of this.#resetTimeouts) clearTimeout(id);
    this.#resetTimeouts = [];
    if (this.#errorTimeout) clearTimeout(this.#errorTimeout);
  }

  // --- Initialization ---

  #parseConfig() {
    this.#discount2 = Number(this.dataset.discount2 || 0);
    this.#discount3 = Number(this.dataset.discount3 || 0);
    this.#pdpIsDefaultTitle = this.dataset.pdpIsDefaultTitle === 'true';
    this.#pdpOptions = {
      option1: this.dataset.pdpOption1Value || '',
      option2: this.dataset.pdpOption2Value || '',
      option3: this.dataset.pdpOption3Value || '',
    };
  }

  #parseBundleVariants() {
    for (let i = 1; i <= 2; i++) {
      const el = this.querySelector(`script[data-bundle-variants="${i}"]`);
      if (!el) continue;
      try {
        this.#bundleVariants.set(i, /** @type {Variant[]} */ (JSON.parse(el.textContent || '[]')));
      } catch { /* variant data unavailable */ }
    }
  }

  #parseBundleMetafields() {
    for (let i = 1; i <= 2; i++) {
      const el = this.querySelector(`script[data-bundle-metafields="${i}"]`);
      if (!el) continue;
      try {
        const raw = JSON.parse(el.textContent || '{}');
        const estimatedMap = /** @type {Map<number, number>} */ (new Map());
        const namesMap = /** @type {Map<number, string>} */ (new Map());
        if (raw.variants) {
          for (const [vid, data] of Object.entries(raw.variants)) {
            const id = parseInt(vid, 10);
            if (!id) continue;
            const d = /** @type {{ estimatedRetail?: number, name?: string }} */ (data);
            if (Number(d.estimatedRetail) > 0) estimatedMap.set(id, Number(d.estimatedRetail));
            namesMap.set(id, String(d.name || ''));
          }
        }
        this.#bundleEstimatedRetailPrices.set(i, estimatedMap);
        this.#bundleVariantNames.set(i, { productTitle: raw.productTitle || '', variants: namesMap });
      } catch { /* metafield data unavailable */ }
    }
  }

  #parsePrimaryVariants() {
    const el = this.querySelector('script[data-primary-variants]');
    if (!el) return;
    try {
      this.#primaryVariants = /** @type {Variant[]} */ (JSON.parse(el.textContent || '[]'));
    } catch { /* primary variant data unavailable */ }
  }

  #parsePrimaryMetafields() {
    const el = this.querySelector('script[data-primary-metafields]');
    if (!el) return;
    try {
      const raw = JSON.parse(el.textContent || '{}');
      const estimatedMap = /** @type {Map<number, number>} */ (new Map());
      if (raw.variants) {
        for (const [vid, data] of Object.entries(raw.variants)) {
          const id = parseInt(vid, 10);
          if (!id) continue;
          const d = /** @type {{ estimatedRetail?: number }} */ (data);
          if (Number(d.estimatedRetail) > 0) estimatedMap.set(id, Number(d.estimatedRetail));
        }
      }
      this.#primaryEstimatedRetailPrices = estimatedMap;
    } catch { /* primary metafield data unavailable */ }
  }

  #parsePrimaryVariantNames() {
    const el = this.querySelector('script[data-primary-variant-names]');
    if (!el) return;
    try {
      const raw = JSON.parse(el.textContent || '{}');
      const map = /** @type {Map<number, string>} */ (new Map());
      if (raw.variants) {
        for (const [vid, name] of Object.entries(raw.variants)) {
          const id = parseInt(vid, 10);
          if (id) map.set(id, String(name || ''));
        }
      }
      this.#primaryVariantNames = { productTitle: raw.productTitle || '', variants: map };
    } catch { /* primary names unavailable */ }
  }

  #initBundleProducts() {
    for (let i = 1; i <= 2; i++) {
      const ref = this.refs[`bundleProduct${i}`];
      const card = /** @type {HTMLElement | null} */ (Array.isArray(ref) ? ref[0] : ref);
      if (!card || !(card instanceof HTMLElement)) continue;

      this.#bundleProducts.set(i, {
        variantId: parseInt(card.dataset.variantId || '0', 10),
        productId: parseInt(card.dataset.productId || '0', 10),
        productHandle: card.dataset.productHandle || '',
        price: parseInt(card.dataset.price || '0', 10),
        sku: card.dataset.sku || '',
        isSelected: true,
        optionMatches: {
          opt1: card.dataset.opt1Matched === 'true',
          opt2: card.dataset.opt2Matched === 'true',
          opt3: card.dataset.opt3Matched === 'true',
        },
        variants: this.#bundleVariants.get(i) || [],
      });
    }
  }

  // --- PDP variant change ---

  /** @param {AbortSignal} signal */
  #listenToPdpVariantChange(signal) {
    document.addEventListener(
      ThemeEvents.variantUpdate,
      /** @param {CustomEvent} event */ (event) => {
        const detail = event.detail;
        if (!detail?.resource) return;

        const myProductId = this.dataset.productId;
        const eventProductId = detail.data?.productId;
        if (myProductId && eventProductId && String(myProductId) !== String(eventProductId)) return;

        const variant = detail.resource;
        if (!variant?.id) return;

        this.#primaryVariantId = variant.id;
        this.#primaryPrice = variant.price || 0;
        this.#primarySku = variant.sku || '';
        this.#pdpIsDefaultTitle = variant.title === 'Default Title';

        const newOptions = {
          option1: variant.option1 || '',
          option2: variant.option2 || '',
          option3: variant.option3 || '',
        };
        const optionsChanged =
          this.#pdpOptions.option1 !== newOptions.option1 ||
          this.#pdpOptions.option2 !== newOptions.option2 ||
          this.#pdpOptions.option3 !== newOptions.option3;
        this.#pdpOptions = newOptions;

        this.#syncDropdownsToVariant(0, variant);
        this.#updatePrimaryProductName(variant.id);
        this.#updatePrimaryProductPricing(variant);
        this.#updateProductImage(/** @type {HTMLElement | null} */ (this.refs.primaryImageWrapper), variant);
        this.#updatePrimaryProductDataset(variant);

        const primaryInputs = this.#getOptionInputs(0);
        if (primaryInputs.length > 0 && this.#primaryVariants.length > 0) {
          this.#updateOptionAvailability(0, this.#readSelectedOptions(primaryInputs), this.#primaryVariants);
        }

        if (optionsChanged) this.#rematchAllBundleProducts();
        if (variant.available === false) this.#setBundleVisibility(false);
        this.#updatePrices();
      },
      { signal },
    );
  }

  #rematchAllBundleProducts() {
    for (const [idx, bp] of this.#bundleProducts) {
      const variants = this.#bundleVariants.get(idx);
      if (!variants?.length) continue;

      const matches = this.#matchOptionsPerProduct(idx, variants);
      bp.optionMatches = matches;

      // Product is eligible if ANY option matches PDP.
      // Needed for products with 2 options like Config + Größe, and also if option order differs.
      const hasAnyMatchedOption = matches.opt1 || matches.opt2 || matches.opt3;

      if (!this.#pdpIsDefaultTitle && !hasAnyMatchedOption && !matches.isComplementary) {
        bp.isSelected = false;
        const card = /** @type {HTMLElement | null} */ (this.refs[`bundleProduct${idx}`]);
        if (card instanceof HTMLElement) card.style.display = 'none';
        continue;
      } else {
        const card = /** @type {HTMLElement | null} */ (this.refs[`bundleProduct${idx}`]);
        if (card instanceof HTMLElement) card.style.display = '';
      }

      const bestVariant = this.#findBestVariant(variants, matches);
      if (bestVariant) {
        bp.variantId = bestVariant.id;
        bp.price = bestVariant.price;
        bp.sku = bestVariant.sku || '';
        this.#syncDropdownsToVariant(idx, bestVariant);
        this.#updateBundleProductDisplay(idx, bestVariant);
      }

      this.#toggleDropdownVisibility(idx, matches);

      const card = /** @type {HTMLElement | null} */ (this.refs[`bundleProduct${idx}`]);
      if (card instanceof HTMLElement) {
        card.dataset.opt1Matched = String(matches.opt1);
        card.dataset.opt2Matched = String(matches.opt2);
        card.dataset.opt3Matched = String(matches.opt3);
      }

      const inputs = this.#getOptionInputs(idx);
      if (inputs.length > 0) this.#updateOptionAvailability(idx, this.#readSelectedOptions(inputs), variants);
    }

    // Hide entire bundle if no sub-product remains eligible
    let anyEligible = false;
    for (const [, bp] of this.#bundleProducts) {
      if (bp.isSelected) { anyEligible = true; break; }
    }
    this.#setBundleVisibility(anyEligible);
  }

  /**
   * Check bundle product options against PDP selected option values.
   * Works even when PDP option order and bundle option order are different.
   * Example: PDP option1 = Größe, bundle option2 = Größe.
   * @param {number} idx
   * @param {Variant[]} variants
   * @returns {OptionMatches}
   */
  #matchOptionsPerProduct(idx, variants) {
    void idx;

    const pdpValues = [
      this.#pdpOptions.option1,
      this.#pdpOptions.option2,
      this.#pdpOptions.option3,
    ]
      .map((v) => String(v || '').trim().toLowerCase())
      .filter(Boolean);

    /** @param {Variant[]} vs @param {1|2|3} pos @returns {string[]} */
    const getOptionValues = (vs, pos) => vs
      .map((v) => (pos === 1 ? v.option1 : pos === 2 ? v.option2 : v.option3)?.toLowerCase())
      .filter(/** @returns {s is string} */ (s) => typeof s === 'string' && s !== '');

    const availableVariants = variants.filter((v) => v.available);

    const allOpt1 = getOptionValues(availableVariants, 1);
    const allOpt2 = getOptionValues(availableVariants, 2);
    const allOpt3 = getOptionValues(availableVariants, 3);

    // If PDP option values share no overlap with any bundle option values at all,
    // the products are complementary (different types) — always eligible, but keep opt flags false
    // so dropdowns are not hidden by #toggleDropdownVisibility.
    const allBundleValues = [...allOpt1, ...allOpt2, ...allOpt3];
    const hasAnyOverlap = allBundleValues.some((bundleValue) =>
      pdpValues.some((pdpValue) => bundleValue.includes(pdpValue) || pdpValue.includes(bundleValue)),
    );
    if (!hasAnyOverlap) return { opt1: false, opt2: false, opt3: false, isComplementary: true };

    /** @param {string[]} optionValues */
    const hasMatch = (optionValues) => {
      if (pdpValues.length === 0) return false;

      return optionValues.some((bundleValue) =>
        pdpValues.some((pdpValue) => bundleValue.includes(pdpValue) || pdpValue.includes(bundleValue)),
      );
    };

    return {
      opt1: this.#pdpIsDefaultTitle ? true : hasMatch(allOpt1),
      opt2: hasMatch(allOpt2),
      opt3: hasMatch(allOpt3),
    };
  }

  /**
   * Find the best available variant given per-option match constraints.
   * Supports products with multiple options like Config + Größe.
   * @param {Variant[]} variants
   * @param {OptionMatches} matches
   * @returns {Variant | undefined}
   */
  #findBestVariant(variants, matches) {
    const o1 = this.#pdpOptions.option1.toLowerCase();
    const o2 = this.#pdpOptions.option2.toLowerCase();
    const o3 = this.#pdpOptions.option3.toLowerCase();

    const pdpValues = [o1, o2, o3].filter(Boolean);
    const skipOpt1 = this.#pdpIsDefaultTitle;

    /**
     * @param {string | undefined} variantValue
     * @param {string} samePositionValue
     */
    const matchesPdpValue = (variantValue, samePositionValue) => {
      const value = String(variantValue || '').toLowerCase();
      if (!value) return false;

      if (samePositionValue && value.includes(samePositionValue)) return true;

      return pdpValues.some((pdpValue) => value.includes(pdpValue) || pdpValue.includes(value));
    };

    const best = variants.find((v) => {
      if (!v.available) return false;

      if (!skipOpt1 && matches.opt1 && !matchesPdpValue(v.option1, o1)) return false;
      if (matches.opt2 && !matchesPdpValue(v.option2, o2)) return false;
      if (matches.opt3 && !matchesPdpValue(v.option3, o3)) return false;

      return true;
    });

    if (best) return best;

    return variants.find((v) => v.available);
  }

  /**
   * @param {number} idx
   * @param {Variant} variant
   */
  #syncDropdownsToVariant(idx, variant) {
    const optionValues = [variant.option1, variant.option2, variant.option3];

    for (let pos = 0; pos < 3; pos++) {
      const value = optionValues[pos];
      if (!value) continue;

      const wrapper = this.querySelector(
        `.buy-the-bundle__custom-dropdown-wrapper[data-bundle-index="${idx}"][data-option-index="${pos}"]`,
      );
      if (!wrapper) continue;

      const input = wrapper.querySelector('.buy-the-bundle__custom-dropdown-input');
      const button = wrapper.querySelector('.buy-the-bundle__custom-dropdown-button');
      const list = wrapper.querySelector('.buy-the-bundle__custom-dropdown-list');
      if (!(input instanceof HTMLInputElement) || !button || !list) continue;

      input.value = value;
      this.#updateDropdownDisplay(button, list, value);
    }
  }

  /**
   * Toggle visibility per option.
   * For products with 2+ options, keep all dropdowns visible.
   * Example: Größe + Config should both stay visible.
   * @param {number} idx
   * @param {OptionMatches} matches
   */
  #toggleDropdownVisibility(idx, matches) {
    const wrappers = Array.from(
      this.querySelectorAll(`.buy-the-bundle__custom-dropdown-wrapper[data-bundle-index="${idx}"]`),
    );

    const shouldHideMatchedDropdowns = wrappers.length === 1;

    for (const wrapper of wrappers) {
      const optionDiv = wrapper.closest('.buy-the-bundle__variant-option');
      if (!optionDiv) continue;

      const posStr = /** @type {HTMLElement} */ (wrapper).dataset.optionIndex;
      const isSize = /** @type {HTMLElement} */ (wrapper).dataset.isSize === 'true';
      const pos = parseInt(posStr || '0', 10);

      let matched = false;
      if (pos === 0) matched = matches.opt1;
      else if (pos === 1) matched = matches.opt2;
      else if (pos === 2) matched = matches.opt3;

      optionDiv.classList.toggle(
        'buy-the-bundle__variant-option--hidden',
        shouldHideMatchedDropdowns && matched && !isSize,
      );
    }
  }

  // --- Internal variant change (dropdown selection) ---

  /** @param {HTMLInputElement} input */
  #handleVariantChange(input) {
    const idx = parseInt(input.dataset.bundleIndex || '', 10);
    if (Number.isNaN(idx)) return;

    if (idx === 0) {
      this.#handlePrimaryVariantChange();
      return;
    }

    const bp = this.#bundleProducts.get(idx);
    const variants = this.#bundleVariants.get(idx);
    if (!bp || !variants) return;

    const allInputs = this.#getOptionInputs(idx);
    this.#updateOptionAvailability(idx, this.#readSelectedOptions(allInputs), variants);

    const refreshed = this.#readSelectedOptions(allInputs);
    const match = this.#findVariantBySelectedOptions(variants, refreshed);

    if (match) {
      bp.variantId = match.id;
      bp.price = match.price;
      bp.sku = match.sku || '';
      this.#updateBundleProductDisplay(idx, match);
      this.#updatePrices();
    }
  }

  #handlePrimaryVariantChange() {
    const variants = this.#primaryVariants;
    if (!variants.length) return;

    const allInputs = this.#getOptionInputs(0);
    this.#updateOptionAvailability(0, this.#readSelectedOptions(allInputs), variants);

    const refreshed = this.#readSelectedOptions(allInputs);
    const match = this.#findVariantBySelectedOptions(variants, refreshed);
    if (!match) return;

    this.#primaryVariantId = match.id;
    this.#primaryPrice = match.price || 0;
    this.#primarySku = match.sku || '';
    this.#pdpIsDefaultTitle = /** @type {any} */ (match).title === 'Default Title';
    this.#pdpOptions = {
      option1: match.option1 || '',
      option2: match.option2 || '',
      option3: match.option3 || '',
    };

    this.#syncDropdownsToVariant(0, match);
    this.#updatePrimaryProductName(match.id);
    this.#updatePrimaryProductPricing(match);
    this.#updateProductImage(/** @type {HTMLElement | null} */ (this.refs.primaryImageWrapper), match);
    this.#updatePrimaryProductDataset(match);
    this.#rematchAllBundleProducts();
    this.#updatePrices();
  }

  /**
   * @param {Variant[]} variants
   * @param {Record<string, string>} selectedOptions
   * @returns {Variant | undefined}
   */
  #findVariantBySelectedOptions(variants, selectedOptions) {
    return variants.find((v) => {
      if (!v.available) return false;
      return (
        (!selectedOptions.option1 || v.option1 === selectedOptions.option1) &&
        (!selectedOptions.option2 || v.option2 === selectedOptions.option2) &&
        (!selectedOptions.option3 || v.option3 === selectedOptions.option3)
      );
    });
  }


  /**
   * @param {number} idx
   * @param {Record<string, string>} selectedOptions
   * @param {Variant[]} variants
   */
  #updateOptionAvailability(idx, selectedOptions, variants) {
    const wrappers = this.querySelectorAll(`.buy-the-bundle__custom-dropdown-wrapper[data-bundle-index="${idx}"]`);

    for (const wrapper of wrappers) {
      const hiddenInput = wrapper.querySelector('.buy-the-bundle__custom-dropdown-input');
      const button = wrapper.querySelector('.buy-the-bundle__custom-dropdown-button');
      const list = wrapper.querySelector('.buy-the-bundle__custom-dropdown-list');
      if (!hiddenInput || !button || !list) continue;

      const pos = /** @type {HTMLInputElement} */ (hiddenInput).dataset.optionPosition;
      if (!pos) continue;

      const items = list.querySelectorAll('.buy-the-bundle__custom-dropdown-item');
      let hasAvailableSelected = false;
      let firstAvailableValue = /** @type {string | null} */ (null);
      const currentValue = /** @type {HTMLInputElement} */ (hiddenInput).value;

      for (const item of items) {
        const el = /** @type {HTMLElement} */ (item);
        const optValue = el.getAttribute('data-value') || '';
        const textEl = el.querySelector('.buy-the-bundle__custom-dropdown-item-text');
        if (!textEl) continue;

        if (!el.dataset.originalText) {
          el.dataset.originalText = (textEl.textContent || '').replace(' - Out of stock', '').trim();
        }

        const test = { ...selectedOptions, [`option${pos}`]: optValue };
        const available = variants.some((v) => {
          if (!v.available) return false;
          return (
            (!test.option1 || v.option1 === test.option1) &&
            (!test.option2 || v.option2 === test.option2) &&
            (!test.option3 || v.option3 === test.option3)
          );
        });

        textEl.textContent = available ? el.dataset.originalText : el.dataset.originalText + ' - Out of stock';
        el.classList.toggle('buy-the-bundle__custom-dropdown-item--disabled', !available);
        el.toggleAttribute('aria-disabled', !available);
        el.setAttribute('data-available', String(available));

        if (available) {
          if (!firstAvailableValue) firstAvailableValue = optValue;
          if (optValue === currentValue) hasAvailableSelected = true;
        }
      }

      if (!hasAvailableSelected && firstAvailableValue && currentValue !== firstAvailableValue) {
        /** @type {HTMLInputElement} */ (hiddenInput).value = firstAvailableValue;
        selectedOptions[`option${pos}`] = firstAvailableValue;
        this.#updateDropdownDisplay(button, list, firstAvailableValue);
      }
    }
  }

  // --- Display updates ---

  /** @param {number} variantId */
  #updatePrimaryProductName(variantId) {
    const el = /** @type {HTMLElement | null} */ (this.refs.primaryProductName);
    if (!el || !this.#primaryVariantNames) return;
    el.textContent = this.#primaryVariantNames.variants.get(variantId) || this.#primaryVariantNames.productTitle || '';
  }

  /** @param {Variant} variant */
  #updatePrimaryProductPricing(variant) {
    const priceEl = /** @type {HTMLElement | null} */ (this.refs.primaryProductPrice);
    const compareEl = /** @type {HTMLElement | null} */ (this.refs.primaryProductComparePrice);
    const discountEl = /** @type {HTMLElement | null} */ (this.refs.primaryProductDiscount);

    if (priceEl) priceEl.textContent = this.#formatMoney(variant.price);

    const estimatedRetail = this.#primaryEstimatedRetailPrices.get(variant.id) || 0;
    const strikeThroughPrice = estimatedRetail > 0 ? estimatedRetail : (variant.compare_at_price || 0);
    const hasDiscount = strikeThroughPrice > variant.price;

    if (compareEl) {
      if (hasDiscount) {
        compareEl.innerHTML = `<span class="visually-hidden">Original price</span><s>${this.#formatMoney(strikeThroughPrice)}</s>`;
        compareEl.style.display = '';
      } else {
        compareEl.style.display = 'none';
      }
    }

    if (discountEl) {
      if (hasDiscount) {
        const pct = Math.round(((strikeThroughPrice - variant.price) * 100) / strikeThroughPrice);
        discountEl.textContent = `(${pct}% ${Theme.translations?.off || 'Off'})`;
        discountEl.style.display = '';
      } else {
        discountEl.style.display = 'none';
      }
    }
  }

  /** @param {Variant} variant */
  #updatePrimaryProductDataset(variant) {
    const card = /** @type {HTMLElement | null} */ (this.refs.primaryProduct);
    if (!(card instanceof HTMLElement)) return;

    card.dataset.variantId = String(variant.id);
    card.dataset.price = String(variant.price || 0);
    card.dataset.comparePrice = String(variant.compare_at_price || 0);
    card.dataset.available = String(variant.available !== false);
    card.dataset.sku = variant.sku || '';
  }


  /**
   * @param {number} idx
   * @param {Variant} variant
   */
  #updateBundleProductDisplay(idx, variant) {
    const priceEl = /** @type {HTMLElement | null} */ (this.refs[`bundleProductPrice${idx}`]);
    const compareEl = /** @type {HTMLElement | null} */ (this.refs[`bundleProductComparePrice${idx}`]);
    const discountEl = /** @type {HTMLElement | null} */ (this.refs[`bundleProductDiscount${idx}`]);
    const card = /** @type {HTMLElement | null} */ (this.refs[`bundleProduct${idx}`]);
    const nameEl = /** @type {HTMLElement | null} */ (this.refs[`bundleProductName${idx}`]);

    if (nameEl) {
      const nameData = this.#bundleVariantNames.get(idx);
      nameEl.textContent = nameData?.variants?.get(variant.id) || nameData?.productTitle || '';
    }

    if (priceEl) priceEl.textContent = this.#formatMoney(variant.price);

    const retailMap = this.#bundleEstimatedRetailPrices.get(idx);
    const estimatedRetail = retailMap?.get(variant.id) || 0;
    const strikePrice = estimatedRetail > 0 ? estimatedRetail : (variant.compare_at_price || 0);
    const hasDiscount = strikePrice > variant.price;

    if (compareEl) {
      if (hasDiscount) {
        compareEl.innerHTML = `<span class="visually-hidden">Original price</span><s>${this.#formatMoney(strikePrice)}</s>`;
        compareEl.style.display = '';
      } else {
        compareEl.style.display = 'none';
      }
    }

    if (discountEl) {
      if (hasDiscount) {
        const pct = Math.round(((strikePrice - variant.price) * 100) / strikePrice);
        discountEl.textContent = `(${pct}% ${Theme.translations?.off || 'Off'})`;
        discountEl.style.display = '';
      } else {
        discountEl.style.display = 'none';
      }
    }

    this.#updateProductImage(/** @type {HTMLElement | null} */ (this.refs[`bundleImageWrapper${idx}`]), variant);
    this.#updateProductImage(/** @type {HTMLElement | null} */ (this.refs[`mobileBundleImageWrapper${idx}`]), variant);

    if (card instanceof HTMLElement) {
      card.dataset.variantId = String(variant.id);
      card.dataset.price = String(variant.price);
      card.dataset.comparePrice = String(variant.compare_at_price || 0);
    }
  }

  /**
   * @param {HTMLElement | null} wrapper
   * @param {Variant} variant
   */
  #updateProductImage(wrapper, variant) {
    if (!wrapper || !variant.featured_image) return;
    const img = wrapper.querySelector('img');
    if (!(img instanceof HTMLImageElement)) return;

    const fi = variant.featured_image;
    const src = fi?.src;
    if (!src) return;

    img.removeAttribute('srcset');
    img.src = src.includes('?') ? `${src}&width=200` : `${src}?width=200`;
    if (fi.alt) img.alt = fi.alt;
  }

  #updatePrices() {
    const totalPrice = /** @type {HTMLElement | null} */ (this.refs.totalPrice);
    const comparePrice = /** @type {HTMLElement | null} */ (this.refs.comparePrice);
    const discountBadgeText = /** @type {HTMLElement | null} */ (this.refs.discountBadgeText);
    const summaryItemCount = /** @type {HTMLElement | null} */ (this.refs.summaryItemCount);
    const savedPrice = /** @type {HTMLElement | null} */ (this.refs.savedPrice);

    let selectedCount = 1;
    let totalBase = this.#primaryPrice;

    for (const [, bp] of this.#bundleProducts) {
      if (bp.isSelected) {
        selectedCount++;
        totalBase += bp.price;
      }
    }

    let pct = 0;

    if (selectedCount >= 3) {
      pct = this.#discount3;
    } else if (selectedCount >= 2) {
      pct = this.#discount2;
    }

    const discountedTotal = Math.round(totalBase * ((100 - pct) / 100));
    const savedAmount = Math.max(totalBase - discountedTotal, 0);
    const hasComparePrice = totalBase > discountedTotal && savedAmount > 0;

    if (summaryItemCount) {
      summaryItemCount.textContent = String(selectedCount);
    }

    if (totalPrice) {
      totalPrice.textContent = this.#formatMoney(discountedTotal);
    }

    if (comparePrice) {
      if (hasComparePrice) {
        comparePrice.innerHTML = `<span class="visually-hidden">Original price</span><s>${this.#formatMoney(totalBase)}</s>`;
        comparePrice.style.display = '';
      } else {
        comparePrice.innerHTML = '';
        comparePrice.style.display = 'none';
      }
    }

    if (savedPrice) {
      if (hasComparePrice) {
        savedPrice.textContent = `(${this.#formatMoney(savedAmount)} gespart)`;
        savedPrice.style.display = '';
      } else {
        savedPrice.textContent = '';
        savedPrice.style.display = 'none';
      }
    }

    if (discountBadgeText) {
      if (pct > 0 && hasComparePrice) {
        discountBadgeText.textContent = `${pct}% RABATT`;
        discountBadgeText.parentElement?.style.setProperty('display', '');
      } else {
        discountBadgeText.textContent = '';
        discountBadgeText.parentElement?.style.setProperty('display', 'none');
      }
    }

    this.#updateItemCount();
  }

  #updateItemCount() {
    let itemCount = /** @type {HTMLElement | null} */ (this.refs.itemCount);
    if (!itemCount) itemCount = /** @type {HTMLElement | null} */ (this.querySelector('.buy-the-bundle__item-count'));
    if (!itemCount) return;

    let count = 1;
    for (const [, bp] of this.#bundleProducts) { if (bp.isSelected) count++; }
    itemCount.textContent = String(count);

    const addToCartText = /** @type {HTMLElement | null} */ (this.refs.addToCartText);
    if (!addToCartText) return;

    const key = count === 1 ? 'item_count_one' : 'item_count_other';
    const template = Theme.translations?.[key] || `{{ count }} Item${count === 1 ? '' : 's'}`;
    const word = template.replace(/\{\{\s*count\s*\}\}/g, '').trim();

    const countSpan = addToCartText.querySelector('.buy-the-bundle__item-count');
    const next = countSpan?.nextSibling;

    if (next?.nodeType === Node.TEXT_NODE) {
      next.textContent = ` ${word})`;
    } else {
      const label = addToCartText.textContent?.split('(')[0]?.trim() || Theme.translations?.add_to_cart || 'Add To Cart';
      addToCartText.innerHTML = `${label} (<span class="buy-the-bundle__item-count" ref="itemCount">${count}</span> ${word})`;
      const el = addToCartText.querySelector('.buy-the-bundle__item-count');
      if (el instanceof HTMLElement) this.refs.itemCount = el;
    }
  }

  /** @param {boolean} visible */
  #setBundleVisibility(visible) {
    this.style.display = visible ? '' : 'none';
    this.toggleAttribute('hidden', !visible);
  }

  // --- Mobile toggle ---

  /** @param {AbortSignal} signal */
  #initMobileToggle(signal) {
    const mobileToggle = /** @type {HTMLElement | null} */ (this.refs.mobileToggle);
    const contentWrapper = /** @type {HTMLElement | null} */ (this.refs.contentWrapper);
    if (!mobileToggle || !contentWrapper) return;

    mobileToggle.addEventListener('click', () => {
      const expanded = mobileToggle.getAttribute('aria-expanded') === 'true';
      mobileToggle.setAttribute('aria-expanded', String(!expanded));
      contentWrapper.classList.toggle('is-expanded', !expanded);
      this.classList.toggle('is-mobile-expanded', !expanded);
    }, { signal });
  }

  // --- Checkboxes ---

  /** @param {AbortSignal} signal */
  /** @param {AbortSignal} signal */
  #initCheckboxes(signal) {
    const syncCheckboxUi = (i) => {
      const checkbox = /** @type {HTMLInputElement | null} */ (this.refs[`bundleCheckbox${i}`]);
      const bp = this.#bundleProducts.get(i);
      if (!checkbox || !bp) return;

      checkbox.checked = bp.isSelected;

      const card = /** @type {HTMLElement | null} */ (this.refs[`bundleProduct${i}`]);
      card?.classList.toggle('buy-the-bundle__product-card--selected', bp.isSelected);

      const mobileImg = /** @type {HTMLElement | null} */ (this.refs[`mobileBundleImageWrapper${i}`]);
      mobileImg?.classList.toggle('buy-the-bundle__mobile-image-wrapper--selected', bp.isSelected);
    };

    const syncAllCheckboxesUi = () => {
      for (let i = 1; i <= 2; i++) {
        syncCheckboxUi(i);
      }

      this.#updatePrices();
    };

    for (let i = 1; i <= 2; i++) {
      const checkbox = /** @type {HTMLInputElement | null} */ (this.refs[`bundleCheckbox${i}`]);
      if (!checkbox) continue;

      checkbox.addEventListener('change', () => {
        const bp = this.#bundleProducts.get(i);
        if (bp) bp.isSelected = checkbox.checked;

        syncCheckboxUi(i);
        this.#updatePrices();
      }, { signal });

      syncCheckboxUi(i);
    }

    window.addEventListener('pageshow', () => {
      syncAllCheckboxesUi();
    }, { signal });

    syncAllCheckboxesUi();
  }

  // --- Custom dropdowns ---

  /** @param {AbortSignal} signal */
  #initCustomDropdowns(signal) {
    for (const wrapper of this.querySelectorAll('.buy-the-bundle__custom-dropdown-wrapper')) {
      const button = wrapper.querySelector('.buy-the-bundle__custom-dropdown-button');
      const list = wrapper.querySelector('.buy-the-bundle__custom-dropdown-list');
      const hiddenInput = wrapper.querySelector('.buy-the-bundle__custom-dropdown-input');
      if (!button || !list || !hiddenInput) continue;

      button.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.#toggleDropdown(button, list);
      }, { signal });

      for (const item of list.querySelectorAll('.buy-the-bundle__custom-dropdown-item')) {
        item.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          if (item.classList.contains('buy-the-bundle__custom-dropdown-item--disabled')) return;
          this.#selectDropdownItem(button, list, hiddenInput, item);
        }, { signal });
      }

      button.addEventListener('keydown', (/** @type {KeyboardEvent} */ e) => {
        if (['Enter', ' ', 'ArrowDown'].includes(e.key)) {
          e.preventDefault();
          if (button.getAttribute('aria-expanded') !== 'true') this.#toggleDropdown(button, list);
        } else if (e.key === 'Escape' && button.getAttribute('aria-expanded') === 'true') {
          e.preventDefault();
          button.setAttribute('aria-expanded', 'false');
          list.setAttribute('hidden', '');
        }
      }, { signal });

      list.addEventListener('keydown', (/** @type {KeyboardEvent} */ e) => {
        this.#handleDropdownListKeydown(e, button, list, hiddenInput);
      }, { signal });
    }

    document.addEventListener('click', () => this.#closeAllDropdowns(), { signal });
  }

  /**
   * @param {Element} button
   * @param {Element} list
   */
  #toggleDropdown(button, list) {
    const open = button.getAttribute('aria-expanded') === 'true';
    this.#closeAllDropdowns();
    if (!open) {
      button.setAttribute('aria-expanded', 'true');
      list.removeAttribute('hidden');
      const target =
        list.querySelector('.buy-the-bundle__custom-dropdown-item--selected') ||
        list.querySelector('.buy-the-bundle__custom-dropdown-item:not(.buy-the-bundle__custom-dropdown-item--disabled)');
      if (target instanceof HTMLElement) target.focus();
    }
  }

  #closeAllDropdowns() {
    for (const btn of this.querySelectorAll('.buy-the-bundle__custom-dropdown-button[aria-expanded="true"]')) {
      btn.setAttribute('aria-expanded', 'false');
    }
    for (const lst of this.querySelectorAll('.buy-the-bundle__custom-dropdown-list:not([hidden])')) {
      lst.setAttribute('hidden', '');
    }
  }

  /**
   * @param {Element} button
   * @param {Element} list
   * @param {Element} hiddenInput
   * @param {Element} item
   */
  #selectDropdownItem(button, list, hiddenInput, item) {
    const value = item.getAttribute('data-value') || '';
    if (hiddenInput instanceof HTMLInputElement) hiddenInput.value = value;

    this.#updateDropdownDisplay(button, list, value);

    button.setAttribute('aria-expanded', 'false');
    list.setAttribute('hidden', '');
    if (button instanceof HTMLElement) button.focus();

    if (hiddenInput instanceof HTMLInputElement) this.#handleVariantChange(hiddenInput);
  }

  /**
   * Shared helper: updates button text, swatch preview, and selected state in list.
   * @param {Element} button
   * @param {Element} list
   * @param {string} value
   */
  #updateDropdownDisplay(button, list, value) {
    const item = list.querySelector(`.buy-the-bundle__custom-dropdown-item[data-value="${CSS.escape(value)}"]`);
    const image = item?.getAttribute('data-image') || '';

    const selectedValueSpan = button.querySelector('.buy-the-bundle__selected-value');
    if (selectedValueSpan) {
      selectedValueSpan.textContent = value;
    } else {
      const text = button.querySelector('.buy-the-bundle__custom-dropdown-text');
      if (text) {
        text.textContent = '';

        const span = document.createElement('span');
        span.className = 'buy-the-bundle__selected-value';
        span.textContent = value;

        text.appendChild(span);
      }
    }

    const swatchPreview = button.querySelector('.buy-the-bundle__swatch-preview');
    if (swatchPreview && button.getAttribute('data-option-index') === '0') {
      const img = swatchPreview.querySelector('img');
      if (img instanceof HTMLImageElement && image) {
        img.src = image;
        img.alt = value;
        img.style.display = '';
      } else if (!image && img instanceof HTMLImageElement) {
        img.style.display = 'none';
      } else if (image) {
        swatchPreview.innerHTML = `<img src="${image}" alt="${value}" width="24" height="24" loading="lazy">`;
      }
    }

    for (const li of list.querySelectorAll('.buy-the-bundle__custom-dropdown-item')) {
      li.classList.remove('buy-the-bundle__custom-dropdown-item--selected');
      li.removeAttribute('aria-selected');
    }
    if (item) {
      item.classList.add('buy-the-bundle__custom-dropdown-item--selected');
      item.setAttribute('aria-selected', 'true');
    }
  }

  /**
   * @param {KeyboardEvent} e
   * @param {Element} button
   * @param {Element} list
   * @param {Element} hiddenInput
   */
  #handleDropdownListKeydown(e, button, list, hiddenInput) {
    const items = Array.from(
      list.querySelectorAll('.buy-the-bundle__custom-dropdown-item:not(.buy-the-bundle__custom-dropdown-item--disabled)'),
    );
    const cur = items.findIndex((i) => i === document.activeElement);

    switch (e.key) {
      case 'ArrowDown': {
        e.preventDefault();
        const next = items[cur + 1];
        if (next instanceof HTMLElement) next.focus();
        break;
      }
      case 'ArrowUp': {
        e.preventDefault();
        const prev = items[cur - 1];
        if (prev instanceof HTMLElement) prev.focus();
        break;
      }
      case 'Enter':
      case ' ':
        e.preventDefault();
        if (cur >= 0 && items[cur]) this.#selectDropdownItem(button, list, hiddenInput, items[cur]);
        break;
      case 'Escape':
        e.preventDefault();
        button.setAttribute('aria-expanded', 'false');
        list.setAttribute('hidden', '');
        if (button instanceof HTMLElement) button.focus();
        break;
      case 'Tab':
        button.setAttribute('aria-expanded', 'false');
        list.setAttribute('hidden', '');
        break;
    }
  }

  // --- Add to cart ---

  /** @param {Event} event */
  async #handleAddToCart(event) {
    const addToCartButton = /** @type {HTMLButtonElement | null} */ (this.refs.addToCartButton);
    if (!addToCartButton) return;

    addToCartButton.disabled = true;
    this.#animateAddToCart();

    /** @type {Array<{ variantId: number; quantity: number; price: number; sku: string }>} */
    const selectedBundleItems = [];
    for (const [, bp] of this.#bundleProducts) {
      if (bp.isSelected && bp.variantId) {
        selectedBundleItems.push({ variantId: bp.variantId, quantity: 1, price: bp.price, sku: bp.sku });
      }
    }

    const totalProductCount = 1 + selectedBundleItems.length;
    let discountPct = 0;
    if (totalProductCount >= 3 && this.#discount3 > 0) discountPct = this.#discount3;
    else if (totalProductCount >= 2 && this.#discount2 > 0) discountPct = this.#discount2;

    const discountMultiplier = (100 - discountPct) / 100;

    /** @type {Record<string, any>} */
    const properties = {};

    if (selectedBundleItems.length > 0) {
      /** @type {string} */
      const uuid = Date.now().toString();

      /** @type {Array<{ id: string; sku: string; qty: number; price: string; is_main: boolean; timestamp: string }>} */
      const group = [
        {
          id: String(this.#primaryVariantId),
          sku: this.#primarySku,
          qty: 1,
          price: String(Math.round(this.#primaryPrice * discountMultiplier)),
          is_main: true,
          timestamp: uuid,
        },
      ];

      for (const item of selectedBundleItems) {
        group.push({
          id: String(item.variantId),
          sku: item.sku,
          qty: item.quantity,
          price: String(Math.round(item.price * discountMultiplier)),
          is_main: false,
          timestamp: uuid,
        });
      }

      properties._group = JSON.stringify(group);
    }

    const hasProperties = Object.keys(properties).length > 0;

    /** @type {Array<{ id: number; quantity: number; properties?: Record<string, any> }>} */
    const items = [{
      id: this.#primaryVariantId,
      quantity: 1,
      ...(hasProperties && { properties }),
    }];

    const sectionIds = /** @type {string[]} */ ([]);
    for (const el of document.querySelectorAll('cart-items-component')) {
      if (el instanceof HTMLElement && el.dataset.sectionId) sectionIds.push(el.dataset.sectionId);
    }

    /** @type {{ items: typeof items; sections?: string }} */
    const payload = { items };
    if (sectionIds.length > 0) payload.sections = sectionIds.join(',');

    try {
      const res = await fetch(Theme.routes.cart_add_url, fetchConfig('json', { body: JSON.stringify(payload) }));
      const data = await res.json();
      if (data.status) {
        this.#showAddToCartError(data.message || 'Error adding to cart');
        this.dispatchEvent(new CartErrorEvent('buy-the-bundle', data.message, data.description, data.errors));
      } else {
        this.dispatchEvent(new CartAddEvent({}, String(this.#primaryVariantId), {
          source: 'buy-the-bundle', itemCount: totalProductCount, productId: this.dataset.productId, sections: data.sections,
        }));
        setTimeout(() => { addToCartButton.disabled = false; }, 500);
      }
    } catch (err) {
      console.error('Add to cart error:', err);
      this.#showAddToCartError('An error occurred. Please try again.');
      addToCartButton.disabled = false;
    } finally {
      cartPerformance.measureFromEvent('add:user-action', event);
    }
  }

  /** @param {string} msg */
  #showAddToCartError(msg) {
    const addToCartButton = /** @type {HTMLButtonElement | null} */ (this.refs.addToCartButton);
    const errorEl = /** @type {HTMLElement | null} */ (this.refs.addToCartTextError);

    if (errorEl) {
      errorEl.classList.remove('hidden');
      let span = errorEl.querySelector('.product-form-error-text');
      if (!span) {
        span = document.createElement('span');
        span.classList.add('product-form-error-text');
        errorEl.appendChild(span);
      }
      span.textContent = msg;

      if (this.#errorTimeout) clearTimeout(this.#errorTimeout);
      this.#errorTimeout = setTimeout(() => {
        errorEl.classList.add('hidden');
        const el = errorEl.querySelector('.product-form-error-text');
        if (el) el.textContent = '';
      }, ERROR_DISPLAY_MS);
    }

    setTimeout(() => { if (addToCartButton) addToCartButton.disabled = false; }, ERROR_REENABLE_MS);
  }

  #animateAddToCart = async function () {
    const btn = /** @type {HTMLButtonElement | null} */ (this.refs.addToCartButton);
    if (!btn) return;
    for (const id of this.#resetTimeouts) clearTimeout(id);
    this.#resetTimeouts = [];
    if (btn.dataset.added !== 'true') btn.dataset.added = 'true';
    await onAnimationEnd(btn);
    const tid = setTimeout(() => {
      btn.removeAttribute('data-added');
      const idx = this.#resetTimeouts.indexOf(tid);
      if (idx > -1) this.#resetTimeouts.splice(idx, 1);
    }, 800);
    this.#resetTimeouts.push(tid);
  };

  // --- Helpers ---

  /**
   * @param {number} idx
   * @returns {NodeListOf<Element>}
   */
  #getOptionInputs(idx) {
    return this.querySelectorAll(`.buy-the-bundle__custom-dropdown-input[data-bundle-index="${idx}"]`);
  }

  /**
   * @param {NodeListOf<Element>} inputs
   * @returns {Record<string, string>}
   */
  #readSelectedOptions(inputs) {
    /** @type {Record<string, string>} */
    const opts = {};
    for (const el of inputs) {
      const inp = /** @type {HTMLInputElement} */ (el);
      if (inp.dataset.optionPosition) opts[`option${inp.dataset.optionPosition}`] = inp.value;
    }
    return opts;
  }

  /**
   * @param {number} cents
   * @returns {string}
   */
  #formatMoney(cents) {
    const amount = Number(cents || 0) / 100;

    return `€${amount.toLocaleString('de-DE', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  }
}

if (!customElements.get('buy-the-bundle-component')) {
  customElements.define('buy-the-bundle-component', BuyTheBundleComponent);
}
